import React from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';

const isBlank = (value) => value === '' || value === null || value === undefined;

/**
 * The MUI number input behind every react-widgets NumberPicker.
 *
 * Two things the widget did that a bare `type="number"` does not, and which callers depend on:
 *
 * - It reported `null` for an empty box. Several handlers branch on that, so an empty box still
 *   reports `null` here rather than ''.
 * - It also clamped to `min`/`max` on commit. That is NOT reproduced: `min`/`max` reach the input
 *   as the native attributes, which mark an out-of-range value invalid and bound the spinner but
 *   do not coerce what was typed. A clamp written here worked in isolation but not through every
 *   caller's `onChange`, and a clamp that only sometimes fires is worse than none.
 *
 * `percent` is the `format={{ style: 'percent' }}` case, and is the one place in this conversion
 * where what a keystroke means changes: see the note on the prop below.
 */
/**
 * @param {{
 *   id?: string,
 *   name?: string,
 *   value?: number | string | null,
 *   onChange?: (value: number | null) => void,
 *   onBlur?: (event: React.FocusEvent) => void,
 *   placeholder?: string,
 *   disabled?: boolean,
 *   min?: number,
 *   max?: number,
 *   step?: number,
 *   percent?: boolean,
 *   error?: boolean,
 *   sx?: object,
 *   slotProps?: object,
 * }} props
 */
export default function NumberTextField({
  id,
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  disabled,
  min,
  max,
  /** Fraction-space step, as the widget took it: 0.01 in `percent` mode is one percentage point. */
  step = 1,
  /**
   * Show and accept whole percents while the form keeps holding the 0–1 fraction.
   *
   * The widget showed the fraction as a percentage but *parsed what you typed as the fraction*:
   * 0.15 gave "15%", while typing 15 meant 1500% and was clamped to "100%". Display and input
   * were in different units. Here both are percent, which is what the labels ("Failure
   * Probability Percentage") already say. The value the form stores is unchanged.
   */
  percent = false,
  error = false,
  sx,
  slotProps,
}) {
  const scale = percent ? 100 : 1;

  const toDisplay = (raw) => {
    if (isBlank(raw)) return '';
    const n = Number(raw);
    if (!Number.isFinite(n)) return '';
    // n * 100 lands on values like 7.000000000000001; round to a hundredth of a percent.
    return percent ? Math.round(n * scale * 100) / 100 : n;
  };

  const report = (displayed) => {
    if (isBlank(displayed)) return onChange(null);
    const n = Number(displayed);
    if (!Number.isFinite(n)) return undefined;
    return onChange(percent ? Math.round((n / scale) * 1e6) / 1e6 : n);
  };

  return (
    <TextField
      id={id}
      name={name}
      type='number'
      value={toDisplay(value)}
      onChange={(event) => report(event.target.value)}
      onBlur={onBlur}
      placeholder={placeholder}
      disabled={disabled}
      error={error}
      sx={sx}
      slotProps={{
        ...slotProps,
        htmlInput: {
          min: min === undefined ? undefined : min * scale,
          max: max === undefined ? undefined : max * scale,
          step: step * scale,
          ...slotProps?.htmlInput,
        },
        input: {
          ...(percent ? { endAdornment: <InputAdornment position='end'>%</InputAdornment> } : {}),
          ...slotProps?.input,
        },
      }}
    />
  );
}
